import { getCoreUtils, nz } from "../../../Admin/kReusableCoreTS/kCoreHandler";
import { kHTMLCore } from "../../../Admin/kReusableCoreTS/kHTMLCore";
import { AppAccount, AppLeaveAnswer, AppLeaveMark, AppLeaveState, AppLeaveStep, AppRelinkState,
    AppSignInState } from "./AppAccount";
import { AppHistoryLine } from "./AppSendHistory";
import { AppSignUpApi } from "./AppSignUpApi";
import { AppSignUp } from "./AppSignUp";
import { AppIcons } from "./AppIcons";
import { AppButton } from "./AppButton";

// AppAccountViews - the markup for `#signin`, `#leave`, the re-link banner and the Sent tab.
//
// ⚠ PURE STRING BUILDERS, AS EVERYWHERE ELSE IN THIS APP, so every one is asserted with no DOM.
// ⚠ THE ELEMENT IDS ARE THE SEAM `AppMain` BINDS - changing one side of a seam produces a button
// that does nothing, so they are constants here and `AppMain` reads them from here.
//
// ⛔ EVERY REFUSAL IS `inkwhy` - INK, NEVER `.trouble`. Law 1: these are validation. The one
// exception is a REFUSED SEND in the Sent list, which is a message that did not go - the only row
// on these screens that is genuinely bad news.

export class AppAccountViews {
    public static readonly SignInNumberId: string = "wac-si-number";
    public static readonly SignInCodeId: string = "wac-si-code";
    public static readonly SignInGoId: string = "wac-signin";
    public static readonly SignInLinkId: string = "wac-si-dropbox";
    public static readonly LeaveBoxId: string = "wac-leave-confirm";
    public static readonly LeaveGoId: string = "wac-leave";
    public static readonly RelinkId: string = "wac-relink";
    public static readonly RelinkGoId: string = "wac-relink-go";
    public static readonly RelinkCopyId: string = "wac-relink-copy";
    public static readonly RelinkCopiedId: string = "wac-relink-copied";
    public static readonly SentHeadId: string = "wac-sent-head";
    public static readonly SentListId: string = "wac-sent";
    public static readonly SentRetryId: string = "wac-sent-retry";

    /** A service sentence as a paragraph in ink, or "" for none. */
    public static InkWhy(Why: string): string {
        const Broken: boolean = getCoreUtils().KErrorMode;
        const None: string = "";
        if (Broken === true) { return None; }
        try {
            const Given: string = Why;
            const Say: string = AppAccount.Sentence(Given);
            if (Say.length === 0) { return None; }
            const Safe: string = kHTMLCore.EscapeHtml(Say);
            return "<p class=\"inkwhy\" role=\"status\">" + Safe + "</p>";
        } catch (e) {
            const Where: string = "AppAccountViews.InkWhy";
            getCoreUtils().KGlobalErrorHandler(e, Where);
            return None;
        }
    }

    /**
     * The two ways in, side by side: sign up (a stranger) and sign in (a subscriber on a new
     * device). ⚠ SIGN UP FIRST - it is the primary action of a public page and the one the
     * signed-out e2e check finds first.
     */
    public static WaysIn = (): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            return "<p class=\"waysin\"><a class=\"btn primary\" href=\"#signup\">Sign up</a>"
                + "<a class=\"btn ghost\" href=\"#" + AppAccount.SignInRoute + "\">"
                + "Sign in on this device</a></p>";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAccountViews.WaysIn");
            return "";
        }
    };

    /** The signed-out card's heading: what the two buttons under it are. */
    public static readonly SignedOutHead: string = "Sign up or sign in";

    /**
     * The page a browser with no archive lands on: one card - a heading, the sentence (never signed in, or the
     * service's own for a sign-in that has lapsed) and the two ways in.
     *
     * ⚠ THE SENTENCE IS THE CARD'S FIRST PARAGRAPH AND KEEPS `signedout` (ink, never the alarm colour) - the
     * signed-out check on the published site reads exactly that. ⚠ `account`, so it is never mistaken for the
     * sign-up card itself (`AppMain.SignUpOnScreen`).
     */
    public static SignedOut = (Why: string): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            return "<section class=\"signup account landing\"><div class=\"panel\">"
                + "<h1>" + kHTMLCore.EscapeHtml(AppAccountViews.SignedOutHead) + "</h1>"
                + "<p class=\"signedout\">" + kHTMLCore.EscapeHtml(AppAccount.Sentence(Why)) + "</p>"
                + AppAccountViews.WaysIn() + "</div></section>";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAccountViews.SignedOut");
            return "";
        }
    };

    /**
     * The footer's account links, for a browser that holds a key: "Import a backup" and "Close my
     * account". ⚠ "" otherwise - a link on a browser that can prove nothing is a dead control.
     *
     * ⭐ "IMPORT A BACKUP" IS HOW A HISTORY ARRIVES AFTER SIGN-UP (2026-09-24) - a subscriber who
     * skipped the import (an iPhone today, an Android next week), or who wants to fill a gap. Until
     * it existed the only way in was sign-up's two-hour secret, and the manual said so.
     */
    public static FootLinks = (Held: boolean): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            if (Held !== true) { return ""; }
            return "  &#183;  <a href=\"#" + AppAccount.ImportRoute + "\">Import a backup</a>";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAccountViews.FootLinks");
            return "";
        }
    };

    /**
     * A button, disabled and saying what it is doing while `Busy`.
     * ⚠ `Kind` is the `.btn` variant: `primary`, `secondary`, or `danger` for the one destructive action
     * on these screens (closing the account).
     */
    private static Button = (Id: string, Word: string, Busy: boolean, Off: boolean, BusyWord: string = "Working",
                             Kind: string = "primary"): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            let Dis: string = "";
            if (Off === true || Busy === true) { Dis = " disabled"; }
            let Label: string = kHTMLCore.EscapeHtml(Word);
            if (Busy === true) {
                Label = "<span class=\"spin\" aria-hidden=\"true\"></span>" + kHTMLCore.EscapeHtml(BusyWord) + "&#8230;";
            }
            return AppButton.Open(Id, Kind) + Dis + ">" + Label + "</button>";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAccountViews.Button");
            return "";
        }
    };

    // ── Sign in on a new device (`#signin`) ──────────────────────────────────────────────────────

    /**
     * ⭐ THE DROPBOX CODE IS THE PROOF. A subscriber on a new device holds no key and no secret; the
     * Dropbox account their archive lives in is the one thing only they can open. So the page is the
     * same paste-the-code dance as the sign-up's Dropbox step, and says to use THAT account.
     */
    public static SignIn = (State: AppSignInState): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            // ⚠ The refusal sits INSIDE step 2, under the button that produced it (it was below the whole list).
            return "<section class=\"signup account\"><div class=\"panel\">"
                + "<h1>Sign in on this device</h1>"
                + "<p class=\"lede\">Already signed up on another phone or computer? Prove it is you"
                + " with the Dropbox your archive lives in.</p>"
                + "<ol class=\"guide\"><li>" + AppAccountViews.SignInLink(State) + "</li>"
                + "<li>" + AppAccountViews.SignInForm(State) + AppAccountViews.InkWhy(State.Why) + "</li></ol>"
                + "<p class=\"hint\">New to WACommander? <a href=\"#signup\">Sign up instead</a>.</p>"
                + "</div></section>";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAccountViews.SignIn");
            return "";
        }
    };

    /** Step 1: the Dropbox link, or why there is none yet. ⛔ New tab + `noopener` - see
     *  `AppSignUp.DropboxLink` for why leaving this page would strand the visitor. */
    private static SignInLink = (State: AppSignInState): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            if (nz(State.Url).length === 0) {
                if (State.Asked !== true) {
                    return "<p class=\"progress\" role=\"status\"><span class=\"spin\" aria-hidden=\"true\"></span>"
                        + "Getting the Dropbox sign-in page&#8230;</p>";
                }
                return AppAccountViews.InkWhy(AppAccount.First(State.UrlWhy, AppSignUpApi.OursWhy()));
            }
            // ⭐ ONE PRIMARY AT A TIME (`AppSignUp.FinishLeads`): this link leads until a code is pasted.
            let Kind: string = "primary";
            if (State.Busy === true) { Kind = "secondary"; }
            return "<a class=\"btn " + Kind + "\" id=\"" + AppAccountViews.SignInLinkId + "\" target=\"_blank\" rel=\"noopener\""
                + " href=\"" + kHTMLCore.EscapeAttr(State.Url) + "\">Open Dropbox and approve</a>"
                + "<span class=\"aside\">Use the Dropbox account you connected when you signed up."
                + " It opens in a new tab - keep this one.</span>"
                + AppSignUp.Frozen();
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAccountViews.SignInLink");
            return "";
        }
    };

    /** Step 2: the number (kept across a refusal) and the code (cleared by the caller). */
    private static SignInForm = (State: AppSignInState): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            return "Then come back here:"
                + "<label class=\"flabel\" for=\"" + AppAccountViews.SignInNumberId + "\">Your WhatsApp"
                + " number</label>"
                + "<div class=\"fieldrow\"><input id=\"" + AppAccountViews.SignInNumberId + "\""
                + " type=\"tel\" inputmode=\"tel\" autocomplete=\"tel\""
                + AppAccountViews.Go(AppAccountViews.SignInGoId) + " value=\""
                + kHTMLCore.EscapeAttr(nz(State.Number)) + "\"></div>"
                + "<label class=\"flabel\" for=\"" + AppAccountViews.SignInCodeId + "\">The code"
                + " Dropbox shows you</label>"
                + "<div class=\"fieldrow\"><input id=\"" + AppAccountViews.SignInCodeId + "\""
                + " type=\"text\" autocomplete=\"off\" autocapitalize=\"none\" autocorrect=\"off\" spellcheck=\"false\""
                + AppAccountViews.Go(AppAccountViews.SignInGoId) + ">"
                // ⚠ SECONDARY UNTIL A CODE IS IN THE BOX (`AppMain.OnFieldInput` swaps the two in place);
                // primary while busy, when the link above is no longer the next thing to press.
                + AppAccountViews.Button(AppAccountViews.SignInGoId, "Sign in", State.Busy, false, "Signing in",
                    AppAccountViews.SignInKind(State))
                + "</div>";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAccountViews.SignInForm");
            return "";
        }
    };

    /** "Sign in" is the main button only while it is working or when there is no Dropbox link to press first. */
    private static SignInKind = (State: AppSignInState): string => {
        if (getCoreUtils().KErrorMode === true) { return "primary"; }
        try {
            if (State.Busy === true || nz(State.Url).length === 0) { return "primary"; }
            return "secondary";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAccountViews.SignInKind");
            return "primary";
        }
    };

    /** The attribute that makes Enter in a field press this button (`AppSignUp.GoAttr`). */
    private static Go = (ButtonId: string): string => {
        return AppSignUp.GoAttrFor(ButtonId, "AppAccountViews.Go");
    };

    // ── Close my account (`#leave`) ──────────────────────────────────────────────────────────────

    /**
     * ⛔ WHAT STAYS THEIRS IS SAID FIRST, BEFORE WHAT GOES. The folder in their own Dropbox is the
     * product's whole point, and somebody who believes leaving deletes their archive will either not
     * leave or panic afterwards.
     */
    public static Leave = (State: AppLeaveState): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            const Answer: AppLeaveAnswer | null = State.Answer;
            let Body: string = "";
            if (Answer !== null && (Answer as AppLeaveAnswer).Left === true) {
                Body = AppAccountViews.Left(Answer as AppLeaveAnswer);
            } else if (nz(State.Target).length === 0) {
                Body = AppAccountViews.InkWhy(AppAccountViews.LeaveWhy(Answer)) + AppAccountViews.WaysIn();
            } else {
                Body = AppAccountViews.LeaveAsk(State);
            }
            return "<section class=\"signup account leave\"><div class=\"panel\">"
                + "<h1>Close your WACommander account</h1>" + Body + "</div></section>";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAccountViews.Leave");
            return "";
        }
    };

    /** Why a browser with no key cannot leave: the service's own sentence if it gave one. */
    private static LeaveWhy = (Answer: AppLeaveAnswer | null): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            if (Answer !== null && nz((Answer as AppLeaveAnswer).Why).length > 0) {
                return (Answer as AppLeaveAnswer).Why;
            }
            return "This browser is not signed in to a WACommander account, so there is nothing here"
                + " to close. Sign in on this device first if you want to close your account from it.";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAccountViews.LeaveWhy");
            return "";
        }
    };

    /** The confirm form, with what will happen and - after a partial attempt - what did. */
    private static LeaveAsk = (State: AppLeaveState): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            const Answer: AppLeaveAnswer | null = State.Answer;
            let Kept: string = AppAccount.KeptSay;
            let Result: string = "";
            if (Answer !== null) {
                if (nz((Answer as AppLeaveAnswer).Kept).length > 0) { Kept = (Answer as AppLeaveAnswer).Kept; }
                Result = AppAccountViews.InkWhy((Answer as AppLeaveAnswer).Why)
                    + AppAccountViews.Steps((Answer as AppLeaveAnswer).Steps);
            }
            // ⭐ OWNER 2026-11-01 (too much text): before the press the kept promise is one compact line whose
            // whole sentence (`KeptSay`, pinned to the service) is its tooltip; after an answer the service's own
            // sentence is shown in full, as before.
            let KeptLine: string = "<p class=\"kept\" title=\"" + kHTMLCore.EscapeAttr(Kept) + "\"><b>Kept:</b> your"
                + " archive in your own Dropbox (Apps/WACommander).<span class=\"sr\"> " + kHTMLCore.EscapeHtml(Kept)
                + "</span></p>";
            if (Answer !== null) { KeptLine = "<p class=\"kept\"><b>Kept:</b> " + kHTMLCore.EscapeHtml(Kept) + "</p>"; }
            return KeptLine + AppAccountViews.Plan() + Result + AppAccountViews.LeaveForm(State);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAccountViews.LeaveAsk");
            return "";
        }
    };

    /** What closing does, one line each. */
    private static Plan = (): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            return "<p class=\"ask\">Removed:</p><ul class=\"plan\">"
                + "<li>the WhatsApp link (Linked devices)</li>"
                + "<li>the Dropbox permission</li>"
                + "<li>everything on our service; your number is released</li></ul>"
                + "<p class=\"hint\" title=\"Coming back means linking your phone, connecting Dropbox and importing"
                + " again.\">This cannot be undone.<span class=\"sr\"> Coming back means linking your phone, connecting"
                + " Dropbox and importing again.</span></p>";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAccountViews.Plan");
            return "";
        }
    };

    /** The line under the form while the account is closing. ⛔ Progress and what comes next - never a duration. */
    public static readonly LeaveBusyWords: string = "Unlinking WhatsApp, withdrawing the Dropbox permission and deleting"
        + " what our service holds. Keep this page open - it lists each step once it has been through them all.";

    /** ⛔ The button is DISABLED until the typed number matches and while a request is out. */
    public static LeaveForm = (State: AppLeaveState): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            let Wait: string = "";
            if (State.Busy === true) {
                // ⛔ NO FORECAST (owner, 2026-09-28: never promise a wait). It said "This can take up to a minute";
                // it now says what is happening and what the page will show - `LeaveBusyWords`, pinned by a test.
                Wait = "<p class=\"progress\" role=\"status\">" + kHTMLCore.EscapeHtml(AppAccountViews.LeaveBusyWords) + "</p>";
            }
            return "<label class=\"flabel\" for=\"" + AppAccountViews.LeaveBoxId + "\">Type your"
                + " WhatsApp number to confirm (starting with +)</label>"
                + "<p class=\"target\">Your number: <b>" + kHTMLCore.EscapeHtml(State.Target) + "</b></p>"
                // ⚠ NO Enter-to-press HERE, ON PURPOSE: this is the one irreversible action on these screens,
                // and it takes a deliberate press of the button. ⭐ `danger`, not the accent fill: the design
                // system's one colour for a destructive action.
                + "<div class=\"fieldrow\"><input id=\"" + AppAccountViews.LeaveBoxId + "\" type=\"tel\""
                + " inputmode=\"tel\" autocomplete=\"off\" value=\""
                + kHTMLCore.EscapeAttr(nz(State.Typed)) + "\">"
                + AppAccountViews.Button(AppAccountViews.LeaveGoId, AppAccount.LeaveWord(State),
                    State.Busy, AppAccount.MayLeave(State) === false, "Closing your account", "danger")
                + "</div>" + Wait;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAccountViews.LeaveForm");
            return "";
        }
    };

    /** The finished page. ⚠ It still says what stays theirs, and offers the way back in. */
    private static Left = (Answer: AppLeaveAnswer): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            let Kept: string = nz(Answer.Kept);
            if (Kept.length === 0) { Kept = AppAccount.KeptSay; }
            return `<div class="finished"><span class="tick" aria-hidden="true">${AppIcons.Svg(AppIcons.Check, 16)}</span>`
                + "<p class=\"closed\">Your account is closed.</p></div>"
                + AppAccountViews.Steps(Answer.Steps)
                + "<p class=\"kept\">" + kHTMLCore.EscapeHtml(Kept) + "</p>"
                + "<div class=\"fieldrow one\"><a class=\"btn ghost\" href=\"#signup\">Sign up again</a>"
                + "</div>";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAccountViews.Left");
            return "";
        }
    };

    /** The service's own step list, each with its own sentence. */
    public static Steps = (Steps: AppLeaveStep[]): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            if (Steps.length === 0) { return ""; }
            const Out: string[] = ["<ul class=\"steps\">"];
            for (const One of Steps) {
                const Mark: AppLeaveMark = AppAccount.MarkOf(One);
                Out.push("<li class=\"lstep " + Mark.Kind + "\"><span class=\"lmark\" aria-hidden=\"true\">"
                    + Mark.Glyph + "</span><span class=\"lsay\">"
                    + kHTMLCore.EscapeHtml(AppAccount.Sentence(One.Say)) + "</span></li>");
            }
            Out.push("</ul>");
            return Out.join("");
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAccountViews.Steps");
            return "";
        }
    };

    // ── The re-link banner ───────────────────────────────────────────────────────────────────────

    /**
     * ⚠ INK, NOT ORANGE: the archive itself is fine, and the banner says so. ⛔ NO "ASK AGAIN" WHILE
     * WAITING OR WHILE A CODE IS SHOWN - each new code cancels the one before it (`AppSignUp.Waiting`).
     */
    public static Relink = (State: AppRelinkState): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            if (State.Show !== true) { return ""; }
            let Inner: string = "";
            if (State.Linked === true) {
                Inner = "<p><b>WhatsApp is linked again.</b> New messages reach your archive from now on.</p>";
            } else if (nz(State.Code).length > 0) {
                Inner = "<p>" + kHTMLCore.EscapeHtml(AppSignUpApi.RelinkWhy()) + "</p>" + AppAccountViews.RelinkCode(State);
            } else if (State.Waiting === true) {
                Inner = "<p>" + kHTMLCore.EscapeHtml(AppSignUpApi.RelinkWhy()) + "</p><p class=\"progress\">"
                    + "Asking WhatsApp for a code&#8230; keep this page open. Asking again would cancel it.</p>";
            } else {
                Inner = "<p>" + kHTMLCore.EscapeHtml(AppSignUpApi.RelinkWhy()) + "</p>"
                    + AppAccountViews.Button(AppAccountViews.RelinkGoId, "Link WhatsApp again", State.Busy, false, "Asking WhatsApp");
            }
            return "<div class=\"relink\" id=\"" + AppAccountViews.RelinkId + "\" role=\"status\">" + Inner
                + AppAccountViews.InkWhy(State.Why) + "</div>";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAccountViews.Relink");
            return "";
        }
    };

    /** The code, large, with Copy - the sign-up's code screen in banner form. */
    private static RelinkCode = (State: AppRelinkState): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            return "<div class=\"codeslab\"><code>" + kHTMLCore.EscapeHtml(State.Code) + "</code>"
                + "<button type=\"button\" class=\"btn secondary\" id=\"" + AppAccountViews.RelinkCopyId
                + "\">Copy</button></div><p class=\"copied\" id=\"" + AppAccountViews.RelinkCopiedId + "\"></p>"
                + "<ol class=\"guide\"><li>On the phone: <b>WhatsApp &gt; Settings &gt; Linked devices"
                + " &gt; Link a device</b>.</li><li>Choose <b>Link with phone number instead</b>.</li>"
                + "<li>Type the code above. This banner moves on by itself.</li></ol>";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAccountViews.RelinkCode");
            return "";
        }
    };

    // ── The Sent tab ─────────────────────────────────────────────────────────────────────────────

    /** The frame the answer is painted into. */
    public static SentShell = (): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            return "<p class=\"sayback\" id=\"" + AppAccountViews.SentHeadId + "\">Loading your sent"
                + " messages&#8230;</p><ul class=\"sent\" id=\"" + AppAccountViews.SentListId + "\"></ul>";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAccountViews.SentShell");
            return "";
        }
    };

    /**
     * The rows. ⛔ EVERY FIELD IS ESCAPED - the preview is text the subscriber typed and the chat
     * name comes from their contacts. ⛔ `trouble` ONLY ON A REFUSED ROW: "Not confirmed" stays ink,
     * because orange reads as "send it again" and that is how a message goes twice.
     */
    public static SentRows = (Lines: AppHistoryLine[]): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            const Out: string[] = [];
            for (const One of Lines) {
                let Cls: string = "sentrow";
                if (One.Trouble === true) { Cls = "sentrow trouble"; }
                let Note: string = "";
                // ⚠ CAPITALISED: a refusal's `why` is written to follow a prefix (`AppAccount.Sentence`).
                if (nz(One.Note).length > 0) {
                    Note = "<p class=\"note\">" + kHTMLCore.EscapeHtml(AppAccount.Sentence(One.Note)) + "</p>";
                }
                Out.push("<li class=\"" + Cls + "\"><b>" + kHTMLCore.EscapeHtml(One.Chat) + "</b> <span class=\"when\">"
                    + kHTMLCore.EscapeHtml(One.When) + "</span> " + AppAccountViews.StateIcon(One) + "<p class=\"preview\">"
                    + kHTMLCore.EscapeHtml(One.Preview) + "</p>" + Note + "</li>");
            }
            return Out.join("");
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAccountViews.SentRows");
            return "";
        }
    };

    /** A sent row's state as an icon (owner 2026-11-02: "Replace with an icon indicating sent status"); the word is its
     *  title and accessible name. */
    public static StateIcon = (One: AppHistoryLine): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            let Icon = AppIcons.Clock;
            if (One.Icon === "sent") { Icon = AppIcons.Check; }
            if (One.Icon === "warn") { Icon = AppIcons.Warning; }
            const Word: string = kHTMLCore.EscapeHtml(One.Status);
            return "<span class=\"state st-" + kHTMLCore.EscapeHtml(One.Icon) + "\" role=\"img\" title=\"" + Word + "\" aria-label=\""
                + Word + "\">" + AppIcons.Svg(Icon, 16) + "</span>";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAccountViews.StateIcon");
            return "";
        }
    };

    /** "Try again" under a list still being restored. ⚠ Wired by `AppMain`, because a link to the
     *  route already showing would not fire `hashchange`. */
    public static SentRetry = (): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            return "<p class=\"sayback\"><a id=\"" + AppAccountViews.SentRetryId + "\" href=\"#"
                + AppAccount.SentRoute + "\">Try again</a></p>";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAccountViews.SentRetry");
            return "";
        }
    };
}
